import {
  type AssignCopilotActionArguments,
  type ClientMemexWorkflow,
  MemexActionType,
  type MemexWorkflowAction,
} from '../workflows/contracts'
import type {
  CreateAutoAssignAgentConfigurationRequest,
  DestroyAutoAssignAgentConfigurationRequest,
  UpdateAutoAssignAgentConfigurationRequest,
} from './contracts'

const DEFAULT_OUTPUT_MODE = 'pull_request'

/** Extract the `get_project_items` action's source-column args from a list of workflow actions. */
function pickGetProjectItemsArgs(actions: Array<MemexWorkflowAction>): {fieldId?: number; fieldOptionId?: string} {
  const action = actions.find(a => a.actionType === MemexActionType.GetProjectItems)
  if (!action) return {}
  return {fieldId: action.arguments.fieldId, fieldOptionId: action.arguments.fieldOptionId}
}

/** Extract the `assign_copilot` action's arguments from a list of workflow actions. */
function pickAssignCopilotArgs(actions: Array<MemexWorkflowAction>): AssignCopilotActionArguments {
  const action = actions.find(a => a.actionType === MemexActionType.AssignCopilot)
  if (!action) return {}
  const {instruction, destinationFieldOptionId, outputMode, model, agentId, allowedRepositories} = action.arguments
  return {
    instruction,
    destinationFieldOptionId,
    outputMode: outputMode ?? DEFAULT_OUTPUT_MODE,
    model,
    agentId,
    allowedRepositories,
  }
}

export function buildCreateAutoAssignAgentConfigurationRequest(
  workflow: ClientMemexWorkflow,
): CreateAutoAssignAgentConfigurationRequest {
  const sourceArgs = pickGetProjectItemsArgs(workflow.actions)
  const assignArgs = pickAssignCopilotArgs(workflow.actions)
  return {
    autopilotConfiguration: {
      name: workflow.name,
      enabled: workflow.enabled,
      ...sourceArgs,
      ...assignArgs,
    },
  }
}

export function buildUpdateAutoAssignAgentConfigurationRequest(
  workflowNumber: number,
  workflow: ClientMemexWorkflow,
): UpdateAutoAssignAgentConfigurationRequest {
  const sourceArgs = pickGetProjectItemsArgs(workflow.actions)
  // The dedicated update endpoint does not accept agentId — it's only set on create.
  const {agentId: _agentId, ...assignArgs} = pickAssignCopilotArgs(workflow.actions)
  return {
    workflowNumber,
    name: workflow.name,
    enabled: workflow.enabled,
    ...sourceArgs,
    ...assignArgs,
  }
}

export function buildDestroyAutoAssignAgentConfigurationRequest(
  workflowNumber: number,
): DestroyAutoAssignAgentConfigurationRequest {
  return {workflowNumber}
}
